import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { initGA } from './GoogleAnalytics';

interface CookieConsentProps {
  onAccept?: () => void;
  onDecline?: () => void;
}

export const CookieConsent: React.FC<CookieConsentProps> = ({
  onAccept,
  onDecline,
}) => {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => setShowBanner(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookie-consent', 'accepted');
    localStorage.setItem('cookie-consent-date', new Date().toISOString());
    setShowBanner(false);
    // Initialize Google Analytics after consent
    initGA();
    if (onAccept) onAccept();
  };

  const handleDecline = () => {
    localStorage.setItem('cookie-consent', 'declined');
    localStorage.setItem('cookie-consent-date', new Date().toISOString());
    setShowBanner(false);
    if (onDecline) onDecline();
  };

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="fixed bottom-4 left-4 right-4 md:left-auto md:right-8 md:bottom-8 z-[100] w-full max-w-[340px]"
        >
            <div className="relative rounded-2xl bg-[#e7f1f1] border border-white p-5 shadow-[0px_12px_24px_rgba(0,0,0,0.1)]">
              {/* Close button */}
              <button
                onClick={handleDecline}
                className="absolute right-3 top-3 text-[#999] hover:text-[#141414] transition-colors"
                aria-label="Close cookie banner"
              >
                <X size={16} />
              </button>

              <div className="flex flex-col gap-3">
                {/* Header */}
                <div className="flex items-center gap-2">
                    <span className="text-xl">🍪</span>
                    <h3 className="font-['Outfit',sans-serif] font-bold text-lg text-[#141414]">
                        Cookie Settings
                    </h3>
                </div>

                {/* Text content */}
                <div>
                  <p className="font-['Public_Sans',sans-serif] text-xs text-[#666] leading-relaxed">
                    Analytics cookies help us see which pages get used. Nothing loads unless you accept.{" "}
                    <Link
                      to="/cookies"
                      className="font-medium text-[#003e5b] underline underline-offset-2 hover:text-[#141414]"
                    >
                      Cookie Policy
                    </Link>
                  </p>
                </div>

                {/* Buttons */}
                <div className="flex flex-row gap-2 w-full pt-1">
                  <button
                    onClick={handleAccept}
                    className="flex-1 bg-[#10fd02] h-9 rounded-full flex items-center justify-center cursor-pointer hover:scale-105 active:scale-95 transition-transform shadow-sm"
                  >
                    <span className="font-['Outfit',sans-serif] font-bold text-[10px] leading-[10px] tracking-[1.2px] uppercase text-[#003e5b]">
                      Accept
                    </span>
                  </button>
                  
                  <button
                    onClick={handleDecline}
                    className="flex-1 bg-white border border-[#ccc] h-9 rounded-full flex items-center justify-center cursor-pointer hover:bg-gray-50 active:scale-95 transition-all"
                  >
                    <span className="font-['Outfit',sans-serif] font-bold text-[10px] leading-[10px] tracking-[1.2px] uppercase text-[#666]">
                      Decline
                    </span>
                  </button>
                </div>
              </div>
            </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};


export default CookieConsent;
